'use client'

import { memo } from 'react'

import { isEqual } from 'lodash'
import { ExternalLink, LinkIcon, OctagonAlert, Send } from 'lucide-react'

import { SplitsLink as Link } from '~/components/Link'
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  EmptyState,
  NetworkDisplay,
  Skeleton,
} from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/component-library/Dropdown'
import { SendNFTButton } from '~/components/forms/send-nft/SendNFTButton'
import { NFTImage } from '~/components/smart-accounts/NFTImage'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg, useTokenVisibilityUpdates } from '~/hooks/org'
import { useNavigation } from '~/hooks/useNavigation'
import { useServerRequests } from '~/hooks/useRequests'
import {
  useOrgSmartAccount,
  useSmartAccountsNFTBalances,
} from '~/hooks/useSmartAccounts'
import { useCanCreateTransaction } from '~/hooks/useTransactions'
import {
  IAddress,
  NFTBlockType,
  NFTMediaType,
  NFTTokenBalance,
  NFTTokenType,
  OrgSmartAccount,
} from '~/types'
import { copyToClipboard } from '~/utils/copyToClipboard'

const HideNFTDialogContent = ({
  nft,
  smartAccount,
  onClose,
}: {
  nft: NFTTokenBalance
  smartAccount: OrgSmartAccount
  onClose: () => void
}) => {
  const { serverPostRequest, isProcessing: isBlockingNFT } = useServerRequests()
  const navigate = useNavigation()
  const { org } = useActiveOrg()
  const { hideNFT } = useTokenVisibilityUpdates()

  const blockNFT = async (nft: NFTTokenBalance) => {
    const params = {
      orgId: smartAccount.orgId,
      chainId: nft.chainId,
      blockType: 'TokenId' as NFTBlockType,
      address: nft.address,
      tokenId: nft.tokenId.toString(),
      nftType: nft.type === NFTTokenType.ERC721 ? 'ERC721' : 'ERC1155',
    }

    const response = await serverPostRequest({
      path: '/api/org/token/visibility/hide-nft',
      params,
      successMessage: `${nft.name} reported as spam`,
      successMessageOptions: {
        action: {
          label: 'Settings',
          onClick() {
            navigate('/settings/team/tokens/')
          },
        },
      },
      errorMessage: 'Failed to report spam',
    })

    if (response) {
      hideNFT(params)
      onClose()
    }
  }

  // Can't send bigint to an event
  const nftForEvent = {
    ...nft,
    amount: nft.amount.toString(),
    tokenId: nft.tokenId.toString(),
  }

  return (
    <SplitsDialogContent
      id="hideNFT"
      title={`Report spam?`}
      variant="confirmation"
      isLoading={isBlockingNFT}
      confirmButton={{
        onClick: () => blockNFT(nft),
        text: 'Confirm',
        eventProperties: {
          nft: nftForEvent,
          chainId: nft.chainId,
        },
      }}
      cancelButton={{
        eventProperties: {
          nft: nftForEvent,
          chainId: nft.chainId,
        },
      }}
    >
      <p>
        Reporting {nft.name} on{' '}
        <NetworkDisplay layout="inline-text" chainId={nft.chainId} /> as spam
        will prevent it from being displayed for all members in the {org?.name}{' '}
        workspace.
      </p>
    </SplitsDialogContent>
  )
}
const SmartAccountNFTBalancesList = ({ address }: { address: IAddress }) => {
  const { smartAccount } = useOrgSmartAccount(address)
  const { balances: nftBalances, fetched: fetchedNftBalances } =
    useSmartAccountsNFTBalances({
      smartAccounts: smartAccount ? [smartAccount] : [],
    })

  return (
    <div className="mt-4">
      {!fetchedNftBalances ? (
        <div className="grid grid-cols-2 gap-4">
          <Skeleton height={8} />
          <Skeleton height={8} />
          <Skeleton height={8} />
          <Skeleton height={8} />
        </div>
      ) : nftBalances.length > 0 ? (
        <div className="grid grid-cols-2 gap-4">
          {nftBalances.map((nft, index) => {
            // We need a unique key. Using ths index as key prevents the
            // NFT component from re-rendering when the index changes.
            const key = [nft.chainId, nft.address, nft.tokenId].join(',')

            return <NFT key={key} nft={nft} index={index} address={address} />
          })}
        </div>
      ) : (
        <EmptyState>No NFTs</EmptyState>
      )}
    </div>
  )
}

const NFT = ({
  nft,
  address,
  index,
}: {
  nft: NFTTokenBalance
  address: IAddress
  index: number
}) => {
  const { setGlobalModalState } = useAuth()
  const { smartAccount } = useOrgSmartAccount(address)
  const { org } = useActiveOrg()
  const canCreateTransaction = useCanCreateTransaction(smartAccount)

  if (!smartAccount) {
    return null
  }

  const isInactiveChain = !(
    org?.smartAccountsChainIds.includes(nft.chainId) ?? false
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div
          key={index}
          className="relative size-32 cursor-pointer overflow-clip rounded-lg hover:opacity-50 data-[state=open]:opacity-50"
        >
          {nft.mediaType === NFTMediaType.video ? (
            <video
              src={nft.mediaURI}
              className="h-full w-full rounded object-cover"
              autoPlay
              loop
              muted
            />
          ) : (
            <NFTImage nft={nft} />
          )}
          {nft.amount > 1n && (
            <div className="absolute right-1 top-1 rounded bg-black/50 px-1.5 py-0.5 text-xs font-medium text-white backdrop-blur">
              {nft.amount.toString()}
            </div>
          )}
          {nft.name && (
            <div className="absolute inset-x-0 bottom-0 bg-black/50 px-2 py-0.5 text-xs font-medium text-white backdrop-blur">
              <div className="line-clamp-1">{nft.name}</div>
            </div>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="left" className="w-48">
        {isInactiveChain && (
          <>
            <div className="flex flex-col gap-2 p-2 px-3">
              <span className="text-sm">
                Enable{' '}
                <NetworkDisplay layout="inline-text" chainId={nft.chainId} /> to
                interact with this NFT.
              </span>
              <Button
                variant="secondaryAction"
                href={{ path: '/settings/team/networks' }}
              >
                Go to settings →
              </Button>
            </div>
            <DropdownMenuSeparator />
          </>
        )}
        {!isInactiveChain && canCreateTransaction && (
          <>
            <SendNFTButton
              sender={smartAccount.address}
              token={nft.address}
              tokenId={nft.tokenId}
              chainId={nft.chainId}
              asChild
            >
              <DropdownMenuItem>
                <Send />
                <div>Send</div>
              </DropdownMenuItem>
            </SendNFTButton>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem
          onClick={() => {
            copyToClipboard(nft.address, 'NFT address copied to clipboard')
          }}
        >
          <LinkIcon />
          <div>Copy address</div>
        </DropdownMenuItem>

        {nft.openseaURL && (
          <Link path={nft.openseaURL} target="tab">
            <DropdownMenuItem>
              <ExternalLink />
              <div>Open in explorer</div>
            </DropdownMenuItem>
          </Link>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setGlobalModalState({
              isOpen: true,
              content: (
                <HideNFTDialogContent
                  nft={nft}
                  smartAccount={smartAccount}
                  onClose={() => {
                    setGlobalModalState({
                      isOpen: false,
                      content: null,
                    })
                  }}
                />
              ),
            })
          }}
        >
          <OctagonAlert />
          <div>Report spam...</div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const SmartAccountNFTBalances = memo(
  function SmartAccountNFTBalances({ address }: { address: IAddress }) {
    const { smartAccount } = useOrgSmartAccount(address)
    const { balances, isPending } = useSmartAccountsNFTBalances({
      smartAccounts: smartAccount ? [smartAccount] : [],
      enabled: false,
    })

    return (
      <div className="flex flex-col space-y-4">
        <Collapsible>
          <CollapsibleTrigger className="w-full justify-between">
            <h3>
              NFTs{' '}
              {balances && !isPending && (
                <span className="font-normal text-secondary">
                  {balances.length}
                </span>
              )}
            </h3>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SmartAccountNFTBalancesList address={address} />
          </CollapsibleContent>
        </Collapsible>
      </div>
    )
  },
  (prevProps, newProps) => {
    const shouldntRerender = isEqual(prevProps, newProps)
    return shouldntRerender
  },
)
